import { useEffect } from 'react';
import { popModal, pushModal } from '@/components/modals';
import {
  ConfirmationModalViewModel,
  useConfirmModalViewModel,
} from '@/components/modals/confirmation-modal';
import useBookAgendaSlotMutation, {
  BookAgendaSlotResponse,
} from '@/hooks/mutations/use-book-agenda-slot';
import { useGetOfficeQuery } from '@/hooks/queries/use-get-office';
import {
  AgendaSlotReservation,
  GetAgendaSlotItemResponse,
} from '@/hooks/queries/use-get-office-agendas-slots';
import type { OfficeAssistantPeriod } from '@/hooks/use-office-assistant-periods-for-slot';
import { type SlotStatus, slotStatusBadgeClassName } from '@/lib/slot-status';
import { doctorFullnameTitleTrad } from '@/utils/doctor';
import { useEventOccurencePeriodInformation } from '@/utils/event-slot';
import { isSlotWithAssistant } from '@/utils/slot-assistant';
import { SheetViewModel } from '../sheet.viewmodel';
import { ViewAgendaSlotEventOccurrence } from '../view-admin-agenda-slot-sheet/view-admin-agenda-slot-sheet.viewmodel';

export type ViewUserAgendaSlotSheetApi = {
  onAfterBooked: (updatedSlot: BookAgendaSlotResponse) => void;
};

const selectBookSlotState = ({
  slotId,
  slotReccurrent,
  eventOccurrence,
  sheetViewModel,
  onAfterBooked,
}: {
  slotId: string;
  slotReccurrent: boolean;
  eventOccurrence: ViewAgendaSlotEventOccurrence;
  sheetViewModel: SheetViewModel;
  onAfterBooked: ViewUserAgendaSlotSheetApi['onAfterBooked'];
}) => {
  const {
    mutate: bookAgendaSlot,
    data: slotBookedResponse,
    isPending: deleting,
    isSuccess,
    isError,
  } = useBookAgendaSlotMutation();
  const { setModalViewModel } = useConfirmModalViewModel({
    isLoading: deleting,
    isSuccess,
    isError,
  });

  const closeModal = (_?: { success: boolean }) => {
    popModal('ConfirmationModal');
    sheetViewModel.api.show();
  };

  const cancelModal = () => {
    popModal('ConfirmationModal');
    sheetViewModel.api.show();
  };

  const submitBookSlot = (id: string) => {
    bookAgendaSlot({
      id,
      occurrence: slotReccurrent
        ? {
            index: eventOccurrence.index,
            startAt: eventOccurrence.start,
            endAt: eventOccurrence.end,
            allday: eventOccurrence.allday,
          }
        : undefined,
    });
  };

  useEffect(() => {
    if (isSuccess) {
      onAfterBooked(slotBookedResponse);
      popModal('ConfirmationModal');

      sheetViewModel.api.close();
    }
  }, [isSuccess]);

  const openModal = () => {
    pushModal('ConfirmationModal', {
      title: 'book-agenda-slot-modal.title',
      description: 'book-agenda-slot-modal.description',
      btnLabel: 'btn.book',
      onConfirm: (viewModel: ConfirmationModalViewModel) => {
        setModalViewModel(viewModel);
        submitBookSlot(slotId);
      },
      onClose: closeModal,
      onCancel: cancelModal,
    });

    sheetViewModel.api.hide();
  };

  return {
    api: {
      open: openModal,
      submit: submitBookSlot,
      close: closeModal,
      cancel: cancelModal,
    },
    data: {
      deleting: deleting,
    },
  };
};

export const getReservationStatus = ({
  reservation,
}: {
  reservation?: AgendaSlotReservation;
}) => {
  const status: SlotStatus = reservation?.status || 'open';

  return {
    label:
      'view-user-agenda-slot-sheet.information.reservation-status.' + status,
    badgeClassName: slotStatusBadgeClassName(status),
    status,
  };
};

/**
 * The assistant periods a substitute is allowed to see on an agenda slot.
 *
 * A substitute only gets the assistant hours of the slots they hold: the
 * substitute agenda also shows the office's open offers, and who staffs them is
 * not their business until the slot is theirs. The substitute view never
 * carries a colleague's reservation, so any reservation on the slot is the
 * requester's own (pending request or confirmed booking).
 *
 * Sans-assistant·e (SA) slots have no hours to show — same AA/SA gate as the
 * admin sheet, so both surfaces agree on when the section appears.
 *
 * @param params.slot - The agenda slot displayed in the sheet.
 * @returns The server-computed periods to render, empty when they must be hidden.
 */
export const selectVisibleAssistantPeriods = ({
  slot,
}: {
  slot: GetAgendaSlotItemResponse;
}): OfficeAssistantPeriod[] => {
  const isOwnSlot = (slot.reservations?.length ?? 0) > 0;
  if (!isOwnSlot) return [];

  const withAssistant = isSlotWithAssistant({
    withAssistant: slot.withAssistant,
    forceWithoutAssistant: slot.forceWithoutAssistant,
    slotType: slot.type,
  });
  if (!withAssistant) return [];

  return slot.assistantPeriods ?? [];
};

export const selectViewUserAgendaSlotSheetViewModel = ({
  slot,
  officeId,
  eventOccurrence,
  sheetViewModel,
  onAfterBooked,
}: {
  slot: GetAgendaSlotItemResponse;
  officeId: string;
  eventOccurrence: ViewAgendaSlotEventOccurrence;
  sheetViewModel: SheetViewModel;
  onAfterBooked: ViewUserAgendaSlotSheetApi['onAfterBooked'];
}) => {
  const officeQuery = useGetOfficeQuery(officeId);

  const getSheetTitle = () => {
    return doctorFullnameTitleTrad(slot.doctorAttending);
  };

  const bookSlot = selectBookSlotState({
    slotId: slot.id,
    slotReccurrent: !!slot.rrule,
    eventOccurrence,
    sheetViewModel,
    onAfterBooked,
  });

  const reservationStatus = getReservationStatus({
    reservation: slot.reservations?.[0],
  });

  const period = useEventOccurencePeriodInformation({
    event: eventOccurrence,
  });

  const retrocessionRate = officeQuery.data
    ? slot.startAt.getDay() === 0 || slot.startAt.getDay() === 6
      ? officeQuery.data.weekendRetrocessionRate
      : slot.withAssistant
        ? officeQuery.data.withAssistantRetrocessionRate
        : officeQuery.data.withoutAssistantRetrocessionRate
    : slot.retrocessionRate;

  return {
    title: getSheetTitle(),
    information: {
      period,
      slotType: slot.type,
      retrocessionRate,
      reservationStatus,
      assistantPeriods: selectVisibleAssistantPeriods({ slot }),
    },
    bookSlot,
  };
};
